import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  WebsiteConfigrationService,
  GalleryImage,
  PaginatedGalleryResponse,
} from '../../services/website/website-configration.service';
import { ConfirmationBoxComponent } from 'src/app/shared/components/confirmation-box/confirmation-box.component';
import { extractErrorMessage } from 'src/app/core/services/uitls/error.util';

interface ExtendedGalleryImage extends GalleryImage {
  thumbnail?: string;
  name?: string;
  file?: File;
}

@Component({
    selector: 'app-service-gallery',
    templateUrl: './service-gallery.component.html',
    styleUrl: './service-gallery.component.scss',
    standalone: false
})
export class ServiceGalleryComponent implements OnInit, OnDestroy {
  @Input() serviceId!: string;
  @Input() initialImages: GalleryImage[] = [];
  @Output() imagesChanged = new EventEmitter<GalleryImage[]>();
  @Output() error = new EventEmitter<string>();

  galleryImages: ExtendedGalleryImage[] = [];
  selectedGalleryIndex: number = 0;
  showGalleryModal: boolean = false;
  draggedImageIndex: number | null = null;

  currentGalleryPage: number = 1;
  totalGalleryPages: number = 0;
  totalGalleryCount: number = 0;
  galleryPageSize: number = 20;

  deleteDialogRef: MatDialogRef<ConfirmationBoxComponent>;

  private hasValidationErrors: boolean = false;

  constructor(
    private websiteConfigrationService: WebsiteConfigrationService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    if (this.serviceId) {
      this.loadGalleryImages();
    }
    if (this.initialImages && this.initialImages.length > 0) {
      this.galleryImages = this.initialImages.map((img) => ({
        ...img,
        thumbnail: img.image,
        name: `صورة ${img.ordering}`,
      }));
    }
  }

  ngOnDestroy(): void {
    if (this.showGalleryModal) {
      document.removeEventListener('keydown', this.handleGalleryKeyPress);
    }
  }

  loadGalleryImages(page: number = 1) {
    this.currentGalleryPage = page;
    this.websiteConfigrationService
      .getGalleryImages(this.serviceId, page)
      .subscribe({
        next: (response: PaginatedGalleryResponse) => {
          this.totalGalleryPages = response.pages_count;
          this.totalGalleryCount = response.count;
          this.currentGalleryPage = response.page;

          if (this.galleryPageSize === 20 && response.results.length > 0) {
            this.galleryPageSize = response.results.length;
          }

          this.galleryImages = response.results.map((img) => ({
            ...img,
            thumbnail: img.image,
            name: `صورة ${img.ordering}`,
          }));

          this.notifyParent();
        },
        error: (error) => {
          this.emitError('حدث خطأ أثناء تحميل الصور');
        },
      });
  }

  nextGalleryPage() {
    if (this.currentGalleryPage < this.totalGalleryPages) {
      this.loadGalleryImages(this.currentGalleryPage + 1);
    }
  }

  previousGalleryPage() {
    if (this.currentGalleryPage > 1) {
      this.loadGalleryImages(this.currentGalleryPage - 1);
    }
  }

  goToGalleryPage(page: number) {
    if (page >= 1 && page <= this.totalGalleryPages) {
      this.loadGalleryImages(page);
    }
  }

  getGalleryPageNumbers(): number[] {
    const pages: number[] = [];
    for (let i = 1; i <= this.totalGalleryPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  onGalleryImageSelected(event: any) {
    const files = event.target.files;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    this.hasValidationErrors = false;

    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (!allowedTypes.includes(file.type.toLowerCase())) {
          this.hasValidationErrors = true;
          this.emitError(
            `نوع الملف غير مدعوم: ${file.name}. يُسمح فقط بـ JPG و PNG و WebP`,
          );
          continue;
        }

        if (file.size > maxSize) {
          this.hasValidationErrors = true;
          this.emitError(
            `حجم الملف كبير جداً: ${file.name}. الحد الأقصى المسموح به هو 5 ميجابايت`,
          );
          continue;
        }

        this.validateAndUploadImage(file, i);
      }
    }
    event.target.value = '';
  }

  private validateAndUploadImage(file: File, index: number) {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const img = new Image();

      img.onload = () => {
        // Image loaded successfully and has valid dimensions
        if (img.width > 0 && img.height > 0) {
          // Image is valid, proceed with upload
          const newImage: ExtendedGalleryImage = {
            id: Date.now() + index,
            image: e.target.result,
            ordering: this.galleryImages.length + 1,
            thumbnail: e.target.result,
            name: file.name,
            file: file,
          };
          this.galleryImages.push(newImage);
          this.notifyParent();

          // Real API: Upload image
          this.addGalleryImage(file, newImage.ordering);
        } else {
          // Invalid image dimensions
          this.hasValidationErrors = true;
          this.emitError(
            `الصورة تالفة أو غير صالحة: ${file.name}. يرجى اختيار صورة صحيحة`,
          );
        }
      };

      img.onerror = () => {
        // Image failed to load - it's corrupted or invalid
        this.hasValidationErrors = true;
        this.emitError(
          `الصورة تالفة أو غير صالحة: ${file.name}. يرجى اختيار صورة صحيحة`,
        );
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      this.hasValidationErrors = true;
      this.emitError(`فشل قراءة الملف: ${file.name}`);
    };

    reader.readAsDataURL(file);
  }

  // Add gallery image via API
  addGalleryImage(file: File, ordering?: number) {
    const formData = new FormData();
    formData.append('image', file);
    if (ordering) {
      formData.append('ordering', ordering.toString());
    }

    this.websiteConfigrationService
      .addGalleryImage(this.serviceId, formData)
      .subscribe({
        next: (response: GalleryImage) => {
          if (!this.hasValidationErrors) {
            this.emitError('');
          }
          this.loadGalleryImages(this.currentGalleryPage);
        },
        error: (error) => {
          this.emitError(extractErrorMessage(error));
          const tempIndex = this.galleryImages.findIndex(
            (img) => img.file === file,
          );
          if (tempIndex !== -1) {
            this.galleryImages.splice(tempIndex, 1);
            this.notifyParent();
          }
        },
      });
  }

  deleteGalleryImage(index: number) {
    const imageToDelete = this.galleryImages[index];

    this.deleteDialogRef = this.dialog.open(ConfirmationBoxComponent, {
      data: {
        id: imageToDelete.id,
        index: index,
        message: 'هل أنت متأكد من حذف هذه الصورة؟',
      },
    });

    this.deleteDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.websiteConfigrationService
          .deleteGalleryImage(this.serviceId, imageToDelete.id)
          .subscribe({
            next: () => {
              const willBeEmpty = this.galleryImages.length === 1;
              const targetPage =
                willBeEmpty && this.currentGalleryPage > 1
                  ? this.currentGalleryPage - 1
                  : this.currentGalleryPage;

              this.loadGalleryImages(targetPage);
            },
            error: (error) => {
              console.error('Error deleting gallery image:', error);
              this.emitError(extractErrorMessage(error));
            },
          });
      }
    });
  }

  // Drag and Drop methods
  onDragStart(event: DragEvent, index: number) {
    event.stopPropagation();
    this.draggedImageIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(
        'text/html',
        (event.target as HTMLElement).innerHTML,
      );
    }
    const target = (event.target as HTMLElement).closest('.gallery-item');
    if (target) {
      target.classList.add('dragging');
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    const target = (event.target as HTMLElement).closest('.gallery-item');
    if (target && !target.classList.contains('dragging')) {
      target.classList.add('drag-over');
    }
  }

  onDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    event.stopPropagation();

    // Remove drag-over class from all items
    const allItems = document.querySelectorAll('.gallery-item');
    allItems.forEach((item) => item.classList.remove('drag-over'));

    if (
      this.draggedImageIndex !== null &&
      this.draggedImageIndex !== dropIndex
    ) {
      // Reorder the array
      const draggedImage = this.galleryImages[this.draggedImageIndex];
      this.galleryImages.splice(this.draggedImageIndex, 1);
      this.galleryImages.splice(dropIndex, 0, draggedImage);

      // Update ordering for all images
      this.galleryImages.forEach((img, idx) => {
        img.ordering = idx + 1;
      });

      this.notifyParent();

      // Call API to update ordering
      this.reorderGalleryImages();
    }
  }

  // Reorder gallery images via API
  reorderGalleryImages() {
    const reorderData = this.galleryImages.map((img) => ({
      id: img.id,
      ordering: img.ordering,
    }));

    this.websiteConfigrationService
      .reorderGalleryImages(this.serviceId, reorderData)
      .subscribe({
        next: () => {
          // Reload gallery to confirm server state
          this.loadGalleryImages(this.currentGalleryPage);
        },
        error: (error) => {
          console.error('Error reordering gallery images:', error);
          this.emitError(extractErrorMessage(error));
          // Reload to restore correct order from server
          this.loadGalleryImages(this.currentGalleryPage);
        },
      });
  }

  onDragEnd() {
    this.draggedImageIndex = null;
    // Remove all drag-related classes
    const draggingElements = document.querySelectorAll('.dragging, .drag-over');
    draggingElements.forEach((el) => {
      el.classList.remove('dragging', 'drag-over');
    });
  }

  onDragLeave(event: DragEvent) {
    event.stopPropagation();
    const target = (event.target as HTMLElement).closest('.gallery-item');
    if (target) {
      target.classList.remove('drag-over');
    }
  }

  openGalleryModal(index: number) {
    this.selectedGalleryIndex = index;
    this.showGalleryModal = true;
    document.addEventListener('keydown', this.handleGalleryKeyPress);
  }

  closeGalleryModal() {
    this.showGalleryModal = false;
    document.removeEventListener('keydown', this.handleGalleryKeyPress);
  }

  handleGalleryKeyPress = (event: KeyboardEvent) => {
    if (!this.showGalleryModal) return;

    switch (event.key) {
      case 'ArrowRight':
        this.nextImage();
        event.preventDefault();
        break;

      case 'ArrowLeft':
        this.previousImage();
        event.preventDefault();
        break;

      case 'Escape':
        this.closeGalleryModal();
        event.preventDefault();
        break;

      default:
        return;
    }
  };

  nextImage() {
    if (this.selectedGalleryIndex < this.galleryImages.length - 1) {
      this.selectedGalleryIndex++;
    }
  }

  previousImage() {
    if (this.selectedGalleryIndex > 0) {
      this.selectedGalleryIndex--;
    }
  }

  triggerGalleryUpload() {
    const fileInput = document.getElementById(
      'galleryFileInput',
    ) as HTMLInputElement;
    fileInput?.click();
  }

  private notifyParent() {
    // Emit clean gallery images without local properties
    const cleanImages = this.galleryImages.map((img) => ({
      id: img.id,
      image: img.image,
      ordering: img.ordering,
    }));
    this.imagesChanged.emit(cleanImages);
  }

  private emitError(message: string) {
    this.error.emit(message);
  }
}
